import RBush from 'rbush';
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { commarize } from 'shared/common/utils/number';
import { BALLOON_ACTIVE_Z_INDEX, BALLOON_HOVERED_Z_INDEX, BALLOON_Z_INDEX } from 'shared/map-search/constants/map';
import { EClustersType } from 'shared/map-search/types/clustersType';

import { BalloonContent } from './BalloonContent';
import { getSpecialPromoMarkup } from './markups/specialPromo';
import { getSpecialPromoMarkup as getSpecialPromoMarkupV2 } from './markups/specialPromoV2';
import { IFeatureProperties, IRBushItem, TFeaturePropertiesRequiredPrice, TFeature } from '../../../types/map';

interface IBalloonManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
  // @todo выпилить эксп CD-159979
  showOnlyPrice: boolean;
  pointType: EClustersType | null;
  isRedesign: boolean;
}

export function defineBalloonManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonManager {
        public readonly objectManager: YMaps.ObjectManager;
        public pointType: EClustersType | null;

        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;
        // @todo выпилить эксп CD-159979
        private showOnlyPrice: boolean;
        private isRedesign: boolean;

        public constructor(options: IBalloonManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;
          this.isRedesign = options.isRedesign;
          // @todo выпилить эксп CD-159979
          this.showOnlyPrice = options.showOnlyPrice;
          this.objectManager = new ymaps.ObjectManager();
          this.pointType = options.pointType;
        }

        public updateBalloons = () => {
          const bounds = this.map.getBounds();
          const rBushItems = this.rbush.search({
            minX: bounds[0][0],
            minY: bounds[0][1],
            maxX: bounds[1][0],
            maxY: bounds[1][1],
          });

          this.objectManager.removeAll();

          const features = rBushItems
            .map(this.prepareBalloon)
            /**
             * @todo разработка в MNZ-700
             * Фильтруем пины, которые должны быть в промо-слое
             */
            .filter(feature => {
              if (!feature) {
                return false;
              }

              const isPulsed = feature.properties.isPulsedGlow;

              /**
               * @description
               * При редизайне показываем балуны либо если у них тип detailed, либо если внутри есть избранное
               */
              if (this.pointType) {
                return (
                  !isPulsed && (this.pointType === EClustersType.Detailed || !!feature.properties.favoriteIds?.length)
                );
              }

              return !isPulsed;
            }) as TFeature[];

          this.objectManager.add({
            type: 'FeatureCollection',
            features,
          });
        };

        public updateBalloonProperties = (featureId: string, properties: TFeaturePropertiesRequiredPrice) => {
          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
            zIndex: this.getBalloonZIndex(properties),
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem) => {
          const { properties } = feature;

          if (properties.type === 'peas') {
            return null;
          }

          if (!properties.minPrice && !(properties.specialPromo || properties.isNewbuildingMainPin)) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
              zIndex: this.getBalloonZIndex(properties),
            },
          };
        };

        private getBalloonZIndex = (properties: IFeatureProperties) => {
          if (properties.isActive) {
            return BALLOON_ACTIVE_Z_INDEX;
          }

          return properties.isHovered ? BALLOON_HOVERED_Z_INDEX : BALLOON_Z_INDEX;
        };

        private prepareBalloonLayout = (properties: TFeaturePropertiesRequiredPrice) => {
          const content = this.prepareBalloonContent(properties);
          // @todo выпилить эксп CD-159979
          const showOnlyPrice = this.showOnlyPrice;
          const pointType = this.pointType;

          return templateLayoutFactory.createClass(content, {
            getShape() {
              // oxlint-disable-next-line typescript/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const wrapperElement = element.firstElementChild;
              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;

              let shiftY = 6;

              if (!pointType) {
                shiftY = 4;
              } else {
                shiftY = pointType === EClustersType.OpenablePeas && properties.count > 1 ? 12 : 6;
              }

              let offsetY = -boundingRect.height - shiftY;

              // @todo выпилить эксп CD-159979
              if (showOnlyPrice) {
                offsetY += 12;
              }

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private prepareBalloonContent(feature: TFeaturePropertiesRequiredPrice) {
          const {
            count,
            minPrice,
            favoriteIds,
            isViewed,
            hasNewobject,
            hasDiscountOffers,
            isActive,
            specialPromo,
            newbuilding,
            isAnyFromKp,
            clusterOfferIds,
            isGlow,
            isHovered,
            isNewbuildingMainPin,
          } = feature;

          // Если спец. проект (например, ПИК)
          if (specialPromo) {
            return this.isRedesign ? getSpecialPromoMarkupV2(feature) : getSpecialPromoMarkup(feature);
          }

          const isClustersRedesign = Boolean(this.pointType);
          const price = minPrice ? `${count > 1 ? 'от ' : ''}${commarize(minPrice)}` : '';
          const isFavorite = clusterOfferIds ? clusterOfferIds.some(id => favoriteIds?.includes(id)) : false;
          const newbuildingId = newbuilding?.id ?? 0;
          const newbuildingText = newbuilding?.text ?? '';
          const isNewbuildingOnProPlus = !!(newbuilding?.isAnyFicheringPlus || isAnyFromKp);

          return renderToStaticMarkup(
            createElement(BalloonContent, {
              count,
              price,
              isClustersRedesign,
              isGlow,
              isFavorite,
              hasNewobject,
              isActive,
              isHovered,
              isViewed,
              isShowOnlyPrice: this.showOnlyPrice,
              pointType: this.pointType,
              newbuildingId,
              newbuildingText,
              hasDiscountOffers,
              isAnyFromKp,
              isNewbuildingMainPin,
              isNewbuildingOnProPlus,
              isNewbuildingFavorite: newbuilding?.isFavorite,
              isRedesign: this.isRedesign,
            }),
          );
        }
      }

      provide(BalloonManager);
    },
  );
}
